import React, { useEffect, useState } from 'react'
import { Bot, Laptop, Users2 } from 'lucide-react'

import { supabase } from '../lib/supabase'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Skeleton } from './ui/skeleton'
import { Switch } from './ui/switch'

// ============================================================================
// Component: AllowedAgentsConfig
//
// New "Allow agents" tab on the Config page (2026-09-06) — a company-wide
// allow-list of which agent *types* members are permitted to connect from
// the Agents page's "+ Add Agent" flow. Only Claude Code has a real
// integration today (see TeamAgents.tsx's own notes); Claude Desktop and
// Cowork are listed so the shape of the feature is visible, but their
// toggles are disabled with a "Coming soon" badge since turning them "on"
// wouldn't do anything real yet.
//
// Storage: a new `enabled_agent_types: string[]` key inside the existing
// teams.standup_config jsonb — same column StandupConfiguration.tsx (the
// Schedule tab) already reads/writes, per explicit decision (no new table).
// To avoid the two tabs clobbering each other's fields on save, this loads
// and re-saves the *entire* raw standup_config object (not a narrowed,
// strictly-typed subset) — same defensive pattern StandupConfiguration.tsx
// itself already relies on for its own extra keys (e.g. onboarding_step).
//
// Scope, explicitly deferred (not built this round): this tab only stores
// the preference. Nothing yet enforces it — mint-install-token and the
// Agents page's "+ Add Agent" button don't check enabled_agent_types, so
// today every member can still add a Claude Code agent regardless of this
// toggle. That enforcement is a natural next step once this UI exists.
// ============================================================================

// ROLE AUDIT (2026-09-29, three-tier permission model -- see
// docs/ALERTING_PERMISSIONS_PLAN.md): explicit decision to leave this
// admin-only, not admin+manager. Which agent types the whole team is allowed
// to connect is company-wide governance, not pod-level oversight.
interface AllowedAgentsConfigProps {
  teamId: string
  isAdmin: boolean
}

interface AgentTypeDef {
  id: string
  label: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  available: boolean
}

const AGENT_TYPES: AgentTypeDef[] = [
  {
    id: 'claude_code',
    label: 'Claude Code',
    description: 'Auto check-ins from the Claude Code CLI Stop hook.',
    icon: Bot,
    available: true,
  },
  {
    id: 'claude_desktop',
    label: 'Claude Desktop',
    description: 'No integration built yet — nothing to connect.',
    icon: Laptop,
    available: false,
  },
  {
    id: 'cowork',
    label: 'Cowork',
    description: 'No integration built yet — nothing to connect.',
    icon: Users2,
    available: false,
  },
]

const DEFAULT_ENABLED = ['claude_code']

export const AllowedAgentsConfig: React.FC<AllowedAgentsConfigProps> = ({ teamId, isAdmin }) => {
  // The raw standup_config row, kept whole (not narrowed to a typed
  // subset) so saving here never drops fields the Schedule tab owns.
  const [rawConfig, setRawConfig] = useState<Record<string, unknown>>({})
  const [enabled, setEnabled] = useState<string[]>(DEFAULT_ENABLED)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    supabase
      .from('teams')
      .select('standup_config')
      .eq('id', teamId)
      .single()
      .then(({ data, error: loadError }) => {
        if (cancelled) return
        if (loadError) {
          setError(loadError.message || 'Failed to load agent settings')
          setLoading(false)
          return
        }
        const config = (data?.standup_config as Record<string, unknown> | null) ?? {}
        setRawConfig(config)
        const list = config.enabled_agent_types
        setEnabled(Array.isArray(list) && list.length > 0 ? (list as string[]) : DEFAULT_ENABLED)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [teamId])

  const toggleType = (typeId: string, checked: boolean) => {
    setEnabled((prev) => (checked ? [...new Set([...prev, typeId])] : prev.filter((id) => id !== typeId)))
  }

  const handleSave = async () => {
    if (!isAdmin) return
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      const { error: saveError } = await supabase
        .from('teams')
        .update({ standup_config: { ...rawConfig, enabled_agent_types: enabled } } as any)
        .eq('id', teamId)

      if (saveError) throw saveError
      setRawConfig((prev) => ({ ...prev, enabled_agent_types: enabled }))
      setSuccess('Saved')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      setError(err?.message || 'Failed to save agent settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="divide-y divide-border border border-border">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center justify-between gap-4 p-4">
              <div className="flex items-start gap-3">
                <Skeleton className="mt-0.5 h-5 w-5 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
              <Skeleton className="h-5 w-9 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Allowed connector types</h2>
        <p className="text-sm text-muted-foreground">
          Choose which AI connector tools members can connect from the Connectors page.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {success && <p className="text-sm text-success">{success}</p>}

      <div className="divide-y divide-border border border-border">
        {AGENT_TYPES.map((type) => {
          const Icon = type.icon
          const checked = type.available && enabled.includes(type.id)
          return (
            <div key={type.id} className="flex items-center justify-between gap-4 p-4">
              <div className="flex items-start gap-3">
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{type.label}</span>
                    {!type.available && (
                      <Badge variant="outline" className="text-xs text-muted-foreground">
                        Coming soon
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{type.description}</p>
                </div>
              </div>
              <Switch
                checked={checked}
                onCheckedChange={(value) => toggleType(type.id, value)}
                disabled={!isAdmin || !type.available}
                aria-label={`Allow ${type.label}`}
              />
            </div>
          )
        })}
      </div>

      {isAdmin ? (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Only team admins can change this.</p>
      )}
    </div>
  )
}

export default AllowedAgentsConfig
